import type { AuthTokens } from '@parking/shared';
import { useAuthStore } from '../store/authStore';

const nativeFetch = globalThis.fetch.bind(globalThis);

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Rotates tenant access+refresh tokens. Single-flight so parallel 401s
 * share one refresh (avoids refresh-token rotation races).
 */
export async function refreshTenantSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const state = useAuthStore.getState();
    const { refreshToken, user, tenantSlug } = state;
    if (!refreshToken || !user) {
      state.clear();
      return false;
    }

    try {
      const res = await nativeFetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        state.clear();
        return false;
      }
      const json = (await res.json()) as { data: AuthTokens };
      state.setSession({
        accessToken: json.data.accessToken,
        refreshToken: json.data.refreshToken,
        user,
        tenantSlug,
        expiresIn: json.data.expiresIn,
      });
      return true;
    } catch {
      state.clear();
      return false;
    }
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/** Refresh if access token is missing expiry info or expires within `skewMs`. */
export async function ensureFreshTenantSession(skewMs = 90_000): Promise<boolean> {
  const { accessToken, refreshToken, accessExpiresAt } = useAuthStore.getState();
  if (!accessToken || !refreshToken) return false;
  if (accessExpiresAt != null && accessExpiresAt - Date.now() > skewMs) {
    return true;
  }
  return refreshTenantSession();
}

export { nativeFetch };
